import React, { useRef, useState, useEffect } from 'react';
import { useSpring, motion, useInView } from 'framer-motion';
import './Project.css';
import { useViewTimer } from '../../utils/useViewTimer';

const ProjectOverview = ({
  title,
  caption,
  description,
  year,
  thumbnail_video,
  thumbnail_image,
  thumbnail,
  index = 0,
  total = 1,
  onNext,
  onPrev,
  onEnterDetail,
  gpuEnabled,
}) => {
  const ref = useRef(null);
  useViewTimer(ref, `project: ${title} (overview)`);
  const isInView = useInView(ref, { once: false, margin: '-20% 0px' });

  const [showContent, setShowContent] = useState(false);
  const videoRef = useRef(null);

  const x = useSpring(0, { stiffness: 150, damping: 20 });
  const y = useSpring(0, { stiffness: 150, damping: 20 });
  const scale = useSpring(1, { stiffness: 150, damping: 20 });

  useEffect(() => {
    if (isInView) {
      setShowContent(false);
      const timeout = setTimeout(() => {
        setShowContent(true);
      }, (title.length - 5) * 60);
      return () => clearTimeout(timeout);
    }
  }, [isInView, title]);

  const handleMouseEnter = () => {
    if (!gpuEnabled && videoRef.current) {
      videoRef.current.play();
      scale.set(1.08);
    }
  };

  const handleMouseLeave = () => {
    if (!gpuEnabled && videoRef.current) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
    }
    scale.set(1);
    x.set(0);
    y.set(0);
  };

  return (
    <motion.div
      ref={ref}
      className="project-overview-container"
      initial={{ opacity: 0, y: 40 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 40 }}
      transition={{ duration: 0.7, ease: 'easeOut' }}
    >
      {showContent && (
        <div className="project-header" key={`header-${index}-${isInView}`}>
          <p className="portfolio-label fade-in-bottom" style={{ animationDelay: '0ms' }}>
            portfolio {year}
          </p>
          <p className="scroll-label fade-in-bottom" style={{ animationDelay: '150ms' }}>
            scroll
          </p>
        </div>
      )}

      <div className="project-content">
        <div className="project-content-inner">
          <h1 key={`title-${index}-${isInView}`} className="project-title fall-in-title">
            {[...title].map((char, i) => (
              <span key={i} style={{ animationDelay: `${i * 60}ms` }}>
                {char === ' ' ? '\u00A0' : char}
              </span>
            ))}
          </h1>

          {showContent && (
            <>
              <p
                key={`caption-${index}-${isInView}`}
                className="project-subtitle fade-in-bottom"
                style={{ animationDelay: '0ms' }}
              >
                {caption?.toUpperCase()} <span className="divider">/</span> YEAR. {year}
              </p>

              <div className="project-overview-details">
                <p
                  key={`desc-${index}-${isInView}`}
                  className="project-description fade-in-bottom"
                  style={{ animationDelay: '300ms' }}
                >
                  {description}
                </p>

                <button
                  key={`button-${index}-${isInView}`}
                  className="visit-button fade-in-bottom"
                  style={{ animationDelay: '600ms' }}
                  onClick={(e) => {
                    e.preventDefault();
                    onEnterDetail();
                  }}
                >
                  VIEW MORE <span className="arrow">↗</span>
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      <motion.div
        key={`media-${index}`}
        className="project-preview-wrapper"
        initial={{ opacity: 0, scale: 0.9 }}
        animate={isInView ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.9 }}
        transition={{ duration: 0.5 }}
      >
        <div
          className={`project-media video-${index}`}
          onClick={onEnterDetail}
          style={{ cursor: 'pointer', position: 'relative' }}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          {thumbnail_video ? (
            <div className="video-wrapper" style={{ position: 'relative' }}>
              <video
                ref={videoRef}
                src={thumbnail_video}
                muted
                loop
                playsInline
                className="project-video"
                autoPlay
              />
              {/* {!gpuEnabled && showOverlay && (
                <div className="video-hover-overlay">
                  <p>Hover to Play</p>
                </div>
              )} */}
            </div>
          ) : (
            <img
              src={thumbnail_image || thumbnail}
              alt={`${title} preview`}
              className="project-image"
            />
          )}
        </div>
      </motion.div>

      <div className="project-counter-circle">
        <div className="dial-overlay" />
        <p className="circle-label-top">PROJECT</p>
        <div className="circle-number">{String(index + 1).padStart(2, '0')}</div>
        <div className="circle-total">{String(total).padStart(2, '0')}</div>
        <button onClick={onPrev} disabled={index === 0} className="arrow-circle left" style={!gpuEnabled ? { cursor: 'pointer' } : {}}>
          ←
        </button>
        <button onClick={onNext} disabled={index === total - 1} className="arrow-circle right" style={!gpuEnabled ? { cursor: 'pointer' } : {}}>
          →
        </button>
        <p className="circle-label-bot">INDEX</p>
      </div>
    </motion.div>
  );
};

export default ProjectOverview;
